// Remove local token manager and always use the global one

// Add this global variable to store page cursors
const pageCursors = {};
const bonusOfferList = document.getElementById('bonus-offer__list');
const postPerPage = bonusOfferList.getAttribute('data-pagination') || 4;

// Add this global variable to store total items estimate
const storeInfo = {
  totalItems: 0,
  estimatedPages: 0
};

// Add filter options object to store available filter values
const filterOptions = {
  brands: [],
  categories: [],
  brandLabels: {},
  categoryLabels: {}
};

// Add this global variable to store all fetched data
const appState = {
  allMetaobjects: [],
  filteredMetaobjects: [],
  currentPage: 1,
  itemsPerPage: postPerPage, // default, will be updated from DOM
  filters: {
    brand: [],
    category: []
  }
};

// Add mobile UI state variables
const uiState = {
  isFilterOpen: false
};

// Modify layout constants to remove min-height values
const layoutConstants = {
  loadingDelay: 300 // milliseconds for minimum loading time to prevent flashing
};

// Add utility functions for debounce and throttle to prevent flickering
// IMPORTANT: Define this before any functions that use it
const utilityFunctions = {
  // Debounce function to prevent rapid firing of events
  debounce: (func, delay) => {
    let debounceTimer;
    return function(...args) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), delay);
    };
  },

  // Throttle function to limit the rate of function execution
  throttle: (func, limit) => {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
};

// Add utility function to convert between GID and clean ID
const urlUtils = {
  // Convert GID to clean ID for URL
  gidToCleanId: (gid) => {
    if (typeof gid === 'string') {
      // Handle Brand GIDs
      if (gid.startsWith('gid://shopify/Brand/')) {
        return gid.replace('gid://shopify/Brand/', '');
      }
      // Handle Category GIDs
      if (gid.startsWith('gid://shopify/Category/')) {
        return gid.replace('gid://shopify/Category/', '');
      }
      // Handle Collection GIDs (legacy support)
      if (gid.startsWith('gid://shopify/Collection/')) {
        return gid.replace('gid://shopify/Collection/', '');
      }
    }
    return gid;
  },

  // Convert clean ID back to Brand GID
  cleanIdToBrandGid: (cleanId) => {
    if (typeof cleanId === 'string' && !cleanId.startsWith('gid://')) {
      return `gid://shopify/Brand/${cleanId}`;
    }
    return cleanId;
  },

  // Convert clean ID back to Category GID
  cleanIdToCategoryGid: (cleanId) => {
    if (typeof cleanId === 'string' && !cleanId.startsWith('gid://')) {
      return `gid://shopify/Category/${cleanId}`;
    }
    return cleanId;
  }
};

// Add a global variable to track our initial data processing
let initialDataProcessing = null;

// Function to process metaobjects data from Liquid (replaces fetchMetaobjects)
function processMetaobjectsData() {
  try {
    // Get data from Liquid that was injected into the page
    const metaobjectsData = window.METAOBJECT_DATA;

    if (!metaobjectsData) {
      console.warn('No metaobject data available from Liquid');
      return {
        metaobjects: {
          edges: []
        }
      };    }

    // Handle different possible data structures from Liquid
    let dataArray = [];

    if (Array.isArray(metaobjectsData)) {
      dataArray = metaobjectsData;
    } else if (metaobjectsData.values && Array.isArray(metaobjectsData.values)) {
      dataArray = metaobjectsData.values;
    } else if (metaobjectsData.edges && Array.isArray(metaobjectsData.edges)) {
      // Already in GraphQL format
      return {
        metaobjects: metaobjectsData
      };
    } else {
      console.warn('Unexpected metaobject data structure:', metaobjectsData);
      return {
        metaobjects: {
          edges: []
        }
      };    }

    // Transform the Liquid data to match the expected GraphQL format
    const transformedData = {      metaobjects: {
        edges: dataArray.map((metaobject, index) => {
          // The metaobject from Liquid should have properties directly
          // We need to convert them to the fields format that the rest of the code expects
          const fields = [];// Convert metaobject properties to fields array
          if (metaobject && typeof metaobject === 'object') {
            Object.keys(metaobject).forEach(key => {
              // Skip system properties and ensure we have valid values
              if (!['id', 'handle', 'type', 'created_at', 'updated_at'].includes(key) &&
                metaobject[key] !== null &&
                metaobject[key] !== undefined) {
                const value = metaobject[key];
                let fieldType = 'single_line_text_field';
                let processedValue = value;
                // Handle special field structures from Liquid
                if (typeof value === 'object' && value !== null) {
                  if (value.type === 'rich_text_field') {
                    fieldType = 'rich_text_field';
                  } else if (value.reference) {
                    fieldType = 'file_reference';
                  } else if (value.id && value.title) {
                    // Handle brand/category objects with direct id and title properties
                    // For brand/category: { id: "gid://shopify/Brand/123", title: "Brand Name" } → "gid://shopify/Brand/123"
                    processedValue = value.id; // Use the ID for filtering
                    fieldType = 'collection_reference';
                  } else if (value.value) {
                    // Handle other objects with .value property
                    if (typeof value.value === 'object' && value.value.title) {
                      processedValue = value.value.title;
                      fieldType = 'collection_reference';
                    } else {
                      processedValue = value.value;
                    }
                  }
                } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
                  fieldType = 'date';
                }

                fields.push({
                  key: key,
                  value: processedValue,
                  type: fieldType,
                  // Store original object for reference if needed
                  ...(typeof value === 'object' && value !== null && value.value ? { originalValue: value } : {})
                });
              }
            });
          }
          // Ensure we always have at least an empty fields array
          if (!Array.isArray(fields)) {
            fields = [];
          }

          const processedNode = {
            node: {
              id: metaobject.id || `temp-${Math.random()}`,
              handle: metaobject.handle || '',
              fields: fields || [] // Ensure fields is always an array
            }
          };

          return processedNode;
        })
      }    };

    return transformedData;
  } catch (error) {
    console.error('Error processing metaobjects data:', error);
    // Return empty structure to prevent further errors
    return {
      metaobjects: {
        edges: []
      }
    };
  }
}

// Function to extract brand and category data directly from metaobject data
function extractBrandCategoryData(metaobjectsData) {
  const brandMap = {};
  const categoryMap = {};

  if (Array.isArray(metaobjectsData)) {
    metaobjectsData.forEach((offer) => {
      // Extract brand data if available
      if (offer.brand && offer.brand.id && offer.brand.title) {
        brandMap[offer.brand.id] = offer.brand.title;
      }

      // Extract category data if available
      if (offer.category && offer.category.id && offer.category.title) {
        categoryMap[offer.category.id] = offer.category.title;
      }
    });
  }

  return { brandMap, categoryMap };
}

// Update the extractFilterOptions function to use direct brand/category data
function extractFilterOptions(metaobjects) {
  const brands = new Set();
  const categories = new Set();

  // Extract brand and category data directly from the original metaobject data
  const { brandMap, categoryMap } = extractBrandCategoryData(window.METAOBJECT_DATA);

  if (metaobjects && metaobjects.edges && Array.isArray(metaobjects.edges)) {
    metaobjects.edges.forEach((edge) => {
      const fields = {};
      if (edge.node && edge.node.fields && Array.isArray(edge.node.fields)) {
        edge.node.fields.forEach((field) => {
          if (field && field.key && field.value) {
            fields[field.key] = field.value;
          }
        });
      }

      // For brand field, check if we have brand data in the original metaobject
      if (fields.brand) {
        brands.add(fields.brand);
      }

      // For category field, check if we have category data in the original metaobject
      if (fields.category) {
        categories.add(fields.category);
      }
    });
  }

  filterOptions.brands = Array.from(brands).sort();
  filterOptions.categories = Array.from(categories).sort();

  // Initialize label maps using the direct brand/category data
  filterOptions.brandLabels = {};
  filterOptions.categoryLabels = {};

  // Map brand GIDs to their titles using the direct data
  filterOptions.brands.forEach(brandGid => {
    if (brandMap[brandGid]) {
      filterOptions.brandLabels[brandGid] = brandMap[brandGid];
    } else {
      filterOptions.brandLabels[brandGid] = brandGid;
    }
  });

  // Map category GIDs to their titles using the direct data
  filterOptions.categories.forEach(categoryGid => {
    if (categoryMap[categoryGid]) {
      filterOptions.categoryLabels[categoryGid] = categoryMap[categoryGid];
    } else {
      filterOptions.categoryLabels[categoryGid] = categoryGid;
    }
  });

  return Promise.resolve();
}

// Add utility function to manage the single loading container
const loadingManager = {
  show: (message = 'Loading promotions...') => {
    const container = document.querySelector('.bonus-offer__loading-container');
    if (container) {
      const messageElement = container.querySelector('p');
      if (messageElement) {
        messageElement.textContent = message;
      }
      container.style.display = 'flex';
      container.style.opacity = '1';
    }
  },

  hide: () => {
    const container = document.querySelector('.bonus-offer__loading-container');
    if (container) {
      container.style.opacity = '0';
      setTimeout(() => {
        container.style.display = 'none';
      }, 300);
    }
  }
};

// New function to process all metaobjects at once (replaces fetchAllMetaobjects)
async function processAllMetaobjects() {
  try {
    // Show loading using the single container
    loadingManager.show();

    // Process data from Liquid
    const data = processMetaobjectsData();

    if (!data || !data.metaobjects) {
      console.warn('No metaobjects data available');
      // Show empty state
      const listContainer = document.querySelector('.bonus-offer__list');
      if (listContainer) {
        listContainer.innerHTML = `
          <div class="no-results" style="display: flex; align-items: center; justify-content: center; padding: 40px;">
            <p>No promotions available at this time.</p>
          </div>
        `;
      }
      loadingManager.hide();
      return Promise.resolve(data);
    }

    // Store all processed metaobjects
    appState.allMetaobjects = data.metaobjects.edges || [];
    appState.filteredMetaobjects = [...appState.allMetaobjects];

    // Extract filter options (this is async because it may fetch collection details)
    await extractFilterOptions(data.metaobjects);

    // Update total count
    storeInfo.totalItems = appState.allMetaobjects.length;

    // Add a small delay for better UX (simulate loading)
    return new Promise(resolve => {
      setTimeout(() => {
        loadingManager.hide();
        resolve(data.metaobjects);
      }, layoutConstants.loadingDelay);
    });
  } catch (error) {
    console.error('Error processing all metaobjects:', error);
    const listContainer = document.querySelector('.bonus-offer__list');
    if (listContainer) {
      listContainer.innerHTML = `
        <div class="error-message" style="display: flex; align-items: center; justify-content: center; flex-direction: column;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
          </svg>
          <p>There was an error loading the promotions. Please try again later.</p>
        </div>
      `;
    }
    loadingManager.hide();
    throw error;
  }
}

// Replace loadPage with improved version that handles clean URLs
function loadPage(page = 1) {
  // Force page to be at least 1
  page = Math.max(1, page);

  // Save current page
  appState.currentPage = page;

  // Update URL to reflect current state - use clean IDs for URL
  const searchParams = new URLSearchParams();
  searchParams.set('page', page.toString());

  // Add filter params with clean IDs using comma-separated values
  Object.entries(appState.filters).forEach(([key, values]) => {
    if (values.length > 0) {
      // Use Set to avoid duplicates and join with commas
      const uniqueValues = [...new Set(values)];
      const cleanValues = uniqueValues.map(value => urlUtils.gidToCleanId(value));
      searchParams.set(key, cleanValues.join(','));
    }
  });

  window.history.replaceState({}, '', `?${searchParams.toString()}`);

  // Calculate pagination slices
  const startIndex = (page - 1) * appState.itemsPerPage;
  const endIndex = startIndex + appState.itemsPerPage;

  // Get current page items
  const pageItems = appState.filteredMetaobjects.slice(startIndex, endIndex);

  // Create page info for rendering pagination
  const pageInfo = {
    hasNextPage: endIndex < appState.filteredMetaobjects.length,
    hasPreviousPage: page > 1,
    startCursor: startIndex > 0 ? String(startIndex) : null,
    endCursor: endIndex < appState.filteredMetaobjects.length ? String(endIndex) : null
  };

  // Show loading state before updating content
  loadingManager.show();

  // Add a small delay before updating content
  setTimeout(() => {
    // Render the current page items
    if (pageItems.length === 0) {
      const listContainer = document.querySelector('.bonus-offer__list');
      const contentContainer = document.querySelector('.bonus-offer__content');
      if (listContainer) {
        listContainer.innerHTML = `
          <div class="no-results">
            No promotions available.
          </div>
        `;
      }
      if (contentContainer) {
        contentContainer.classList.add('no-promotions');
      }
    } else {
      // Create a metaobjects structure similar to what the API would return
      const metaobjects = {
        edges: pageItems,
        pageInfo: pageInfo
      };

      renderMetaobjects(metaobjects);
    }
    // Always update statistics and pagination based on filteredMetaobjects length
    renderStatistics(appState.filteredMetaobjects.length, page);
    renderPagination(pageInfo, page);

    // Render the filter UI
    renderFilterUI(appState.filters);

    // Update filter visibility based on available offers
    toggleFilterVisibility();

    // Remove any filtering visual state
    const listContainer = document.querySelector('.bonus-offer__list');
    if (listContainer) {
      listContainer.classList.remove('is-filtering');
    }

    // Hide loading
    loadingManager.hide();
  }, 100); // Minimum delay to ensure loading is visible
}

// Define applyFiltersWithDebounce ONCE - Remove all other declarations
const applyFiltersWithDebounce = utilityFunctions.debounce((expandedSections = {}) => {
  // Show loading with filter-specific message and ensure minimum visibility
  loadingManager.show();

  // Add visual feedback for filtering
  const listContainer = document.querySelector('.bonus-offer__list');
  if (listContainer) {
    listContainer.classList.add('is-filtering');
  }

  // Collect all checked brand filters
  const brandCheckboxes = document.querySelectorAll('input[name="brand"]:checked');
  const brandValues = [...new Set(Array.from(brandCheckboxes).map((cb) => cb.value))];

  // Collect all checked category filters
  const categoryCheckboxes = document.querySelectorAll('input[name="category"]:checked');
  const categoryValues = [...new Set(Array.from(categoryCheckboxes).map((cb) => cb.value))];

  // Update app state filters
  appState.filters = {
    brand: brandValues,
    category: categoryValues
  };

  // Apply filters to all metaobjects
  if (brandValues.length > 0 || categoryValues.length > 0) {
    appState.filteredMetaobjects = appState.allMetaobjects.filter((edge) => {
      const fields = {};

      // Create a map of fields for easy access
      if (edge.node && edge.node.fields && Array.isArray(edge.node.fields)) {
        edge.node.fields.forEach((field) => {
          if (field && field.key && field.value) {
            fields[field.key] = field.value;
          }
        });
      }

      // Check if this metaobject matches all active filters
      let matchesBrand = true;
      let matchesCategory = true;

      if (brandValues.length > 0) {
        matchesBrand = brandValues.includes(fields.brand);
      }

      if (categoryValues.length > 0) {
        matchesCategory = categoryValues.includes(fields.category);
      }

      return matchesBrand && matchesCategory;
    });
  } else {
    // No filters active, show all items
    appState.filteredMetaobjects = [...appState.allMetaobjects];
  }

  // Update URL with filters using clean IDs - start with fresh URLSearchParams
  const searchParams = new URLSearchParams();

  // Use Set to avoid duplicates, then add filter values to URL with clean IDs
  const uniqueBrands = [...new Set(brandValues)];
  const uniqueCategories = [...new Set(categoryValues)];

  // Add comma-separated values to URL
  if (uniqueBrands.length > 0) {
    const cleanBrands = uniqueBrands.map(brand => urlUtils.gidToCleanId(brand));
    searchParams.set('brand', cleanBrands.join(','));
  }

  if (uniqueCategories.length > 0) {
    const cleanCategories = uniqueCategories.map(category => urlUtils.gidToCleanId(category));
    searchParams.set('category', cleanCategories.join(','));
  }

  // Reset to page 1 when filters change
  searchParams.set('page', '1');
  appState.currentPage = 1;

  // Use replaceState instead of pushState to avoid adding to browser history on each filter
  window.history.replaceState({}, '', `?${searchParams.toString()}`);

  // Ensure minimum loading time for better UX and visual feedback
  setTimeout(() => {
    // Remove filtering visual state
    const listContainer = document.querySelector('.bonus-offer__list');
    if (listContainer) {
      listContainer.classList.remove('is-filtering');
    }

    // Reload the first page with new filters, preserving expanded sections
    loadPage(1);

    // Update filter visibility based on available offers
    toggleFilterVisibility();

    // Restore expanded state after page load
    setTimeout(() => {
      const brandSection = document.getElementById('brand-filters');
      const categorySection = document.getElementById('category-filters');

      if (brandSection && expandedSections.brand) {
        brandSection.classList.add('show-all');
        const button = document.querySelector('button[data-target="brand-filters"]');
        if (button) button.classList.add('showing-all');
      }

      if (categorySection && expandedSections.category) {
        categorySection.classList.add('show-all');
        const button = document.querySelector('button[data-target="category-filters"]');
        if (button) button.classList.add('showing-all');
      }
    }, 10);
  }, 300); // Minimum loading time for better UX
}, 300); // 300ms debounce delay

// Replace the existing applyFilters function with the debounced version
function applyFilters(expandedSections = {}) {
  applyFiltersWithDebounce(expandedSections);
}

// This function starts the data processing as early as possible
function initiateEarlyDataProcessing() {
  // Start processing immediately and store the promise
  initialDataProcessing = processAllMetaobjects().catch(error => {
    console.error('Early data processing failed:', error);
    // We'll handle the error display when rendering
    return null;
  });
  return initialDataProcessing;
}

// Start the data processing immediately when the script loads
initiateEarlyDataProcessing();

// Update loadAllItems to handle comma-separated values
function loadAllItems() {
  const searchParams = new URLSearchParams(window.location.search);

  // Parse comma-separated filter parameters
  const brandFilters = searchParams.get('brand')?.split(',').filter(Boolean) || [];
  const categoryFilters = searchParams.get('category')?.split(',').filter(Boolean) || [];

  // Clear the params and rebuild them
  searchParams.delete('brand');
  searchParams.delete('category');
  searchParams.delete('page');

  // Set pageView=all
  searchParams.set('pageView', 'all');

  // Add back all filter parameters with clean IDs
  brandFilters.forEach(brand => {
    searchParams.append('brand', brand);
  });

  categoryFilters.forEach(category => {
    searchParams.append('category', category);
  });

  // Update URL with all parameters
  window.history.pushState({}, '', `?${searchParams.toString()}`);

  // // Show loading state
  loadingManager.show();

  // Apply a small delay to ensure loading indicator is visible
  setTimeout(() => {
    // Show all items without pagination
    if (appState.filteredMetaobjects.length === 0) {
      const listContainer = document.querySelector('.bonus-offer__list');
      if (listContainer) {
        listContainer.innerHTML = `
          <div class="no-results" style="display: flex; align-items: center; justify-content: center;">
            No promotions available.
          </div>
        `;
      }
      const contentContainer = document.querySelector('.bonus-offer__content');
      if (contentContainer) {
        contentContainer.classList.add('no-promotions');
      }
    } else {
      // Create a metaobjects structure for rendering
      const metaobjects = {
        edges: appState.filteredMetaobjects,
        pageInfo: {
          hasNextPage: false,
          hasPreviousPage: false
        }
      };

      renderMetaobjects(metaobjects);
    }

    // Always render statistics with accurate filtered count
    renderStatistics(appState.filteredMetaobjects.length, 'all');

    // Update filter visibility based on available offers
    toggleFilterVisibility();

    // Hide pagination
    const paginationContainer = document.querySelector('.bonus-offer__pagination');
    if (paginationContainer) {
      paginationContainer.style.display = 'none';
    }

    // Hide loading
    loadingManager.hide();
  }, 300); // Slight delay for better UX
}

// Update the DOMContentLoaded event handler to handle clean URLs from the browser
document.addEventListener('DOMContentLoaded', async () => {
  try {
    const container = document.querySelector('.bonus-offer__container');
    if (!container) {
      console.error('Container .bonus-offer__container not found');
      return;
    }

    // Get pagination setting from DOM
    const bonusOfferList = document.getElementById('bonus-offer__list');
    if (bonusOfferList) {
      const paginationSetting = bonusOfferList.getAttribute('data-pagination');
      if (paginationSetting) {
        appState.itemsPerPage = parseInt(paginationSetting, 10) || 4;
      }
    }

    // Check if pagination container exists, create if needed
    if (!document.querySelector('.bonus-offer__pagination')) {
      const paginationDiv = document.createElement('div');
      paginationDiv.classList.add('bonus-offer__pagination');
      container.appendChild(paginationDiv);
    }

    // Check if stats container exists, create if needed
    if (!document.querySelector('.bonus-offer__stats')) {
      const statsDiv = document.createElement('div');
      statsDiv.classList.add('bonus-offer__stats');

      // Insert stats before pagination
      const pagination = document.querySelector('.bonus-offer__pagination');
      if (pagination && pagination.parentNode) {
        pagination.parentNode.insertBefore(statsDiv, pagination);
      } else {
        // Fallback: append to container if pagination not found
        container.appendChild(statsDiv);
      }
    }

    // Check if filter container exists (it should already be in the HTML)
    // If not found, create it at the beginning of the container
    if (!document.querySelector('.bonus-offer__filter')) {
      const filterDiv = document.createElement('div');
      filterDiv.classList.add('bonus-offer__filter');

      // Insert as first child if container has children, otherwise just append
      if (container.firstChild) {
        container.insertBefore(filterDiv, container.firstChild);
      } else {
        container.appendChild(filterDiv);
      }
    }

    // Read URL params for initial state and convert clean IDs back to GIDs
    const searchParams = new URLSearchParams(window.location.search);
    appState.currentPage = parseInt(searchParams.get('page')) || 1;

    // Initialize filters from URL, handling comma-separated values
    const brandParam = searchParams.get('brand');
    const categoryParam = searchParams.get('category');

    appState.filters = {
      brand: brandParam ? brandParam.split(',').map(brand => urlUtils.cleanIdToBrandGid(brand)) : [],
      category: categoryParam ? categoryParam.split(',').map(category => urlUtils.cleanIdToCategoryGid(category)) : []
    };

    // Use the data that was already being processed
    let metaobjectsData;
    if (initialDataProcessing) {
      metaobjectsData = await initialDataProcessing;
    } else {
      // If for some reason the early processing didn't happen, do it now
      metaobjectsData = await processAllMetaobjects();
    }

    // IMPORTANT: First initialize the filter UI with the data, then apply any filters
    // Ensure all metaobjects are filtered properly based on URL parameters
    if (appState.filters.brand.length > 0 || appState.filters.category.length > 0) {
      // Apply the filters but don't re-render yet (we'll do that below)
      appState.filteredMetaobjects = appState.allMetaobjects.filter((edge) => {
        const fields = {};

        if (edge.node && edge.node.fields && Array.isArray(edge.node.fields)) {
          edge.node.fields.forEach((field) => {
            if (field && field.key && field.value) {
              fields[field.key] = field.value;
            }
          });
        }

        let matchesBrand = true;
        let matchesCategory = true;

        if (appState.filters.brand.length > 0) {
          matchesBrand = appState.filters.brand.includes(fields.brand);
        }

        if (appState.filters.category.length > 0) {
          matchesCategory = appState.filters.category.includes(fields.category);
        }

        return matchesBrand && matchesCategory;
      });
    } else {
      // No filters active, show all items
      appState.filteredMetaobjects = [...appState.allMetaobjects];
    }

    // Check if pageView=all is specified first
    if (searchParams.get('pageView') === 'all') {
      loadAllItems();
    } else {
      // Otherwise load the current page with filtered metaobjects
      loadPage(appState.currentPage);
    }

    // Always render the filter UI with the correct state, regardless of pageView mode
    renderFilterUI(appState.filters);

    // Initialize filter sections
    initializeFilterSections();

    // Hide filter if there are no offers
    toggleFilterVisibility();

  } catch (error) {
    console.error('Error during initialization:', error);
    document.querySelector('.bonus-offer__list').innerHTML = `
      <div class="error-message">
        There was an error initializing the page. Please try again later.<br>
      </div>
    `;
  }
});

// Add utility function to hide/show filter based on filter options availability
function toggleFilterVisibility() {
  const filterElement = document.querySelector('.bonus-offer__filter');
  const filterToggleElement = document.querySelector('.bonus-offer__filter-toggle');
  
  if (!filterElement) return;
  
  // Check if there are any offers available (check all offers, not just filtered ones)
  const hasOffers = appState.allMetaobjects && appState.allMetaobjects.length > 0;
  
  // Check if there are any categories or brands available for filtering
  const hasBrands = filterOptions.brands && filterOptions.brands.length > 0;
  const hasCategories = filterOptions.categories && filterOptions.categories.length > 0;
  const hasFilterOptions = hasBrands || hasCategories;
  
  // Show filter only if there are offers AND there are filter options (brands or categories)
  if (hasOffers && hasFilterOptions) {
    filterElement.style.display = '';
    if (filterToggleElement) {
      filterToggleElement.style.display = '';
    }
  } else {
    filterElement.style.display = 'none';
    if (filterToggleElement) {
      filterToggleElement.style.display = 'none';
    }
  }
}

// Update renderPagination to work with client-side pagination
function renderPagination(pageInfo, currentPage) {
  const paginationContainer = document.querySelector('.bonus-offer__pagination');
  if (!paginationContainer) return;

  paginationContainer.innerHTML = '';
  paginationContainer.style.display = ''; // Show pagination by default

  // Calculate total pages
  const totalPages = Math.ceil(appState.filteredMetaobjects.length / appState.itemsPerPage);

  if (totalPages <= 1) {
    // Hide pagination if only one page
    paginationContainer.style.display = 'none';
    return;
  }

  // Create pagination container
  const paginationNav = document.createElement('nav');
  paginationNav.className = 'pagination';
  paginationNav.setAttribute('role', 'navigation');
  paginationNav.setAttribute('aria-label', 'Pagination');

  const paginationList = document.createElement('ul');
  paginationList.className = 'pagination-list';

  // Previous button
  if (currentPage > 1) {
    const prevItem = document.createElement('li');
    const prevLink = document.createElement('a');
    prevLink.className = 'pagination-previous';
    prevLink.innerHTML = `<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <mask id="mask0_4467_25549" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="25">
                          <rect y="0.5" width="24" height="24" fill="#D9D9D9"/>
                          </mask>
                          <g mask="url(#mask0_4467_25549)">
                          <path d="M15.5006 18.5961L5.92383 12.5001L15.5006 6.40405V18.5961Z" fill="#F00D0D"/>
                          </g>
                          `;
    prevLink.href = '#';
    prevLink.addEventListener('click', (e) => {
      e.preventDefault();
      loadPage(currentPage - 1);
    });
    prevItem.appendChild(prevLink);
    paginationList.appendChild(prevItem);
  }

  // Page numbers
  const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const endPage = Math.min(totalPages, Math.max(5, currentPage + 2));

  for (let i = startPage; i <= endPage; i++) {
    const pageItem = document.createElement('li');
    const pageLink = document.createElement('a');
    pageLink.className = 'pagination-link';
    pageLink.textContent = i.toString();
    if (i === currentPage) {
      pageLink.className += ' is-current';
      pageLink.setAttribute('aria-current', 'page');
    } else {
      pageLink.href = '#';
      pageLink.addEventListener('click', (e) => {
        e.preventDefault();
        loadPage(i);
      });
    }

    pageItem.appendChild(pageLink);
    paginationList.appendChild(pageItem);
  }

  // Next button
  if (pageInfo.hasNextPage) {
    const nextItem = document.createElement('li');
    const nextLink = document.createElement('a');
    nextLink.className = 'pagination-next';
    nextLink.innerHTML = `<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <mask id="mask0_4467_25547" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="25">
                          <rect y="0.5" width="24" height="24" fill="#D9D9D9"/>
                          </mask>
                          <g mask="url(#mask0_4467_25547)">
                          <path d="M8.5 18.5961V6.40405L18.0767 12.5001L8.5 18.5961Z" fill="#F00D0D"/>
                          </g>
                          `;
    nextLink.href = '#';
    nextLink.addEventListener('click', (e) => {
      e.preventDefault();
      loadPage(currentPage + 1);
    });
    nextItem.appendChild(nextLink);
    paginationList.appendChild(nextItem);
  }

  paginationNav.appendChild(paginationList);
  paginationContainer.appendChild(paginationNav);
}

// Function to render rich text content
function renderRichText(richText) {
  try {
    if (!richText) return '';

    let richTextData;

    // Handle if the input is already an object
    if (typeof richText === 'object') {
      richTextData = richText;
    } else {
      // Try to parse as JSON
      try {
        richTextData = JSON.parse(richText);
      } catch (e) {
        // If not valid JSON, return as-is
        return String(richText);
      }
    }

    // Handle specific structure from the API
    if (richTextData && richTextData.type === 'root' && Array.isArray(richTextData.children)) {
      return richTextData.children.map(node => {
        // Handle paragraphs
        if (node.type === 'paragraph') {
          const content = (node.children || []).map(child => {
            // Handle text nodes
            if (child.type === 'text') {
              return child.value;
            }
            // Handle links
            else if (child.type === 'link' && child.url) {
              const linkText = (child.children || [])
                .map(linkChild => linkChild.value || '')
                .join('');
              return `<a href="${child.url}" target="_blank" rel="noopener">${linkText}</a>`;
            }
            return '';
          }).join('');

          return `<p>${content}</p>`;
        }
        // Handle headings
        else if (node.type && node.type.startsWith('heading')) {
          const level = node.depth || parseInt(node.type.charAt(node.type.length - 1)) || 2;
          const content = (node.children || [])
            .map(child => child.value || '')
            .join('');
          return `<h${level}>${content}</h${level}>`;
        }
        // Handle lists
        else if (node.type === 'list') {
          const listType = node.ordered ? 'ol' : 'ul';
          const listItems = (node.children || []).map(item => {
            if (item.type === 'listItem') {
              const itemContent = (item.children || [])
                .map(itemChild => {
                  if (itemChild.type === 'paragraph') {
                    return (itemChild.children || [])
                      .map(paraChild => paraChild.value || '')
                      .join('');
                  }
                  return '';
                })
                .join('');
              return `<li>${itemContent}</li>`;
            }
            return '';
          }).join('');
          return `<${listType}>${listItems}</${listType}>`;
        }

        return '';
      }).join('');
    }

    // Fallback to string representation
    return typeof richTextData === 'string'
      ? richTextData
      : JSON.stringify(richTextData);

  } catch (error) {
    console.warn('Error rendering rich text:', error);
    return String(richText || ''); // Safe fallback
  }
}

// Fix the formatDate function (remove duplicate)
function formatDate(dateString) {
  try {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch (error) {
    console.warn('Error formatting date:', dateString, error);
    return dateString; // Return the original string if formatting fails
  }
}

// Add missing renderMetaobjects function
function renderMetaobjects(metaobjects) {
  const container = document.querySelector('.bonus-offer__list');
  const contentContainer = document.querySelector('.bonus-offer__content');

  if (!container) {
    console.error('Could not find container element .bonus-offer__list');
    return;
  }

  if (!metaobjects || !metaobjects.edges || metaobjects.edges.length === 0) {
    container.innerHTML = '<div class="no-results">No promotions available.</div>';
    if (contentContainer) {
      contentContainer.classList.add('no-promotions');
    }
    return;
  }

  // Remove no-promotions class if it exists
  if (contentContainer) {
    contentContainer.classList.remove('no-promotions');
  }

  container.innerHTML = '';
  metaobjects.edges.forEach((edge, index) => {

    const metaobject = edge.node;
    const fields = {};

    if (!metaobject.fields || !Array.isArray(metaobject.fields)) {
      console.error(`Metaobject ${index + 1} has no fields array:`, metaobject);
      return;
    }

    metaobject.fields.forEach((field) => {
      if (!field || !field.key) {
        console.warn('Skipping invalid field:', field);
        return;
      }
      fields[field.key] = field.value;

      // Store the field type for special handling during rendering
      fields[`${field.key}_type`] = field.type;
    });

    // Render the metaobject item
    try {
      const itemHtml = `
        <div class="bonus-offer__item" data-id="${metaobject.id || ''}">
          <div class="bonus-offer__item--image">
            ${
        fields.image
          ? `<a href="${fields.url}" title="${
            fields.short_description || ''
          }" class="bonus-offer__item--image-link">
                    <img src="${fields.image}" alt="${fields?.imageAlt || ''}" width="100%" height="auto">
                  </a>`
          : `<a href="${fields.url}" title="${
            fields.short_description || ''
          }" class="bonus-offer__item--image-link"><div class="placeholder-svg"><svg class="placeholder-svg placeholder" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path><path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5-10.1c-20.1 1.7-36.7 4.8-49.1 7.9l-16.9-16.9 26.3-26.3c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l27.5 27.5v7.8zm-68.9 15.5c9.7-3.5 33.9-10.9 68.9-13.8v13.8h-68.9zm68.9-72.7v46.8l-26.2-26.2c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-26.3 26.3-.9-.9c-1.9-1.9-4.5-3-7.3-3s-5.4 1.1-7.3 3l-18.8 18.8V225h101.4z"></path><path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6s-2.9 6.6-6.6 6.6-6.6-2.9-6.6-6.6 3-6.6 6.6-6.6z"></path></svg></div></a>`
      }
          </div>
          <div class="bonus-offer__item--content">
            <p class="bonus-offer__item--content-title">
              <a href="${fields.url}">
                ${fields.short_description || 'Untitled Promotion'}
              </a>
            </p>
            <div class="bonus-offer__item--content-description">
              ${
        fields.basic_description
          ? renderRichText(fields.basic_description)
          : ''
      }
            </div>
            ${
        fields.expiry
          ? `<p class="bonus-offer__item--content-expiry">
                Offer Ends: ${formatDate(fields.expiry)}
              </p>`
          : ''
      }
            ${
        fields.redemption_cut_off
          ? `<p class="bonus-offer__item--content-redemption">
                Redemption Cut-Off: ${formatDate(fields.redemption_cut_off)}
              </p>`
          : ''
      }
          </div>
        </div>
      `;
      container.innerHTML += itemHtml;
    } catch (error) {
      console.error(`Error rendering metaobject ${index + 1}:`, error);
    }
  });
}

// Add missing renderStatistics function
function renderStatistics(itemCount, currentPage) {
  let statsContainer = document.querySelector('.bonus-offer__stats');
  if (!statsContainer) {
    statsContainer = document.createElement('div');
    statsContainer.classList.add('bonus-offer__stats');
    const parentContainer = document.querySelector('.bonus-offer__bottom');
    const paginationDiv = document.querySelector('.bonus-offer__pagination');
    if (parentContainer && paginationDiv) {
      parentContainer.insertBefore(statsContainer, paginationDiv);
    }
  }

  // Use itemCount directly since it already contains the filtered count passed from loadPage/loadAllItems
  const displayCount = itemCount;

  // Determine if we should show the View All button
  const showViewAll =
    currentPage !== 'all' && // Not already in View All mode
    displayCount > appState.itemsPerPage; // Have more items than fit on one page

  // Update the UI with the correct count and conditionally show the view all button
  statsContainer.innerHTML = `
    ${displayCount > 0 ? `
      <div class="bonus-offer__stats-count">
        <span>${displayCount}</span> ${displayCount > 1 ? 'items' : 'item '} found
      </div>
    ` : ''}
    ${showViewAll ? `
    <div class="bonus-offer__stats-actions">
      <button class="bonus-offer__view-all">View All</button>
    </div>
    ` : ''}
  `;

  const viewAllButton = statsContainer.querySelector('.bonus-offer__view-all');
  if (viewAllButton) {
    viewAllButton.addEventListener('click', () => {
      loadAllItems();

      // Ensure filter UI is rendered after switching to "View All" mode
      setTimeout(() => {
        renderFilterUI(appState.filters);
      }, 300);
    });
  }
}

// Add a function to count items per filter value
function countItemsPerFilterValue() {
  const brandCounts = {};
  const categoryCounts = {};

  // Initialize all possible values with zero counts
  filterOptions.brands.forEach((brand) => (brandCounts[brand] = 0));
  filterOptions.categories.forEach((category) => (categoryCounts[category] = 0));
  // Count occurrences in all metaobjects
  appState.allMetaobjects.forEach((edge) => {
    const fields = {};

    // Safety check to ensure fields exists and is an array
    if (edge.node && edge.node.fields && Array.isArray(edge.node.fields)) {
      edge.node.fields.forEach((field) => {
        fields[field.key] = field.value;
      });
    }

    // Increment brand count if it exists
    if (fields.brand && brandCounts.hasOwnProperty(fields.brand)) {
      brandCounts[fields.brand]++;
    }

    // Increment category count if it exists
    if (fields.category && categoryCounts.hasOwnProperty(fields.category)) {
      categoryCounts[fields.category]++;
    }
  });

  return { brandCounts, categoryCounts };
}

// Update the generateFilterOptions function to include counts
function generateFilterOptions(options, values, labels, counts, type) {
  let optionsHtml = '';
  const MAX_VISIBLE_OPTIONS = 5;
  options.forEach((option, index) => {
    const isVisible = index < MAX_VISIBLE_OPTIONS ? '' : 'hidden-option';
    // Get the count for this option, default to 0 if missing
    const count = counts[option] || 0;

    // Ensure option is a string for HTML generation
    const optionStr = String(option);
    const safeId = optionStr.replace(/\//g, '-').replace(/\s+/g, '-').toLowerCase();

    optionsHtml += `
      <div class="bonus-offer__filter-option ${isVisible}" data-filter-idx="${index}">
        <input type="checkbox" id="${type}-${safeId}" 
          name="${type}" value="${option}" ${values.includes(option) ? 'checked' : ''}>
        <label for="${type}-${safeId}">
          ${labels[option] || `Unknown ${type.charAt(0).toUpperCase() + type.slice(1)}`}
          <span class="bonus-offer__filter-count">${count}</span>
        </label>
      </div>
    `;
  });

  // Add a "Show All" button if there are more than MAX_VISIBLE_OPTIONS
  if (options.length > MAX_VISIBLE_OPTIONS) {
    optionsHtml += `
      <div class="bonus-offer__filter-show-more">
        <button type="button" class="bonus-offer__filter-show-all" data-target="${type}-filters">
          <svg width="24" height="24" class="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_1306_29633" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
            <rect y="-0.5" width="24" height="24" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_1306_29633)">
            <path d="M11.9999 14.5538L6.34619 8.9L7.39994 7.84625L11.9999 12.4463L16.5999 7.84625L17.6537 8.9L11.9999 14.5538Z" fill="#F00D0D"/>
            </g>
          </svg>
          <span class="show-text">Show All </span>
          <span class="hide-text">Show Less</span>
        </button>
      </div>
    `;
  }

  return optionsHtml;
}

// Update renderFilterUI to use the counts and preserve expanded state
function renderFilterUI(currentFilters = {}) {
  // Track which filter sections are expanded
  const expandedSections = {};

  // Store the current expansion state before re-rendering
  const brandFiltersElement = document.getElementById('brand-filters');
  const categoryFiltersElement = document.getElementById('category-filters');

  if (brandFiltersElement) {
    expandedSections.brand = brandFiltersElement.classList.contains('show-all');
  }
  if (categoryFiltersElement) {
    expandedSections.category = categoryFiltersElement.classList.contains('show-all');
  }

  const filterContainer = document.querySelector('.bonus-offer__filter');
  if (!filterContainer) {
    console.error('Filter container not found');
    return;
  }

  // Check if mobile filter toggle button exists, create if not
  if (!document.querySelector('.bonus-offer__filter-toggle')) {
    const filterToggle = document.createElement('button');
    filterToggle.className = 'bonus-offer__filter-toggle';
    filterToggle.innerHTML = `<span class="icon-filter">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <mask id="mask0_4219_44651" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
        <rect width="24" height="24" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_4219_44651)">
        <path d="M11.9998 20.75C11.7871 20.75 11.609 20.6781 11.4655 20.5343C11.3218 20.3906 11.25 20.2125 11.25 20V16C11.25 15.7875 11.3219 15.6094 11.4658 15.4658C11.6096 15.3219 11.7878 15.25 12.0003 15.25C12.2129 15.25 12.391 15.3219 12.5345 15.4658C12.6782 15.6094 12.75 15.7875 12.75 16V17.25H20C20.2125 17.25 20.3906 17.3219 20.5343 17.4658C20.6781 17.6096 20.75 17.7878 20.75 18.0003C20.75 18.2129 20.6781 18.391 20.5343 18.5345C20.3906 18.6782 20.2125 18.75 20 18.75H12.75V20C12.75 20.2125 12.6781 20.3906 12.5343 20.5343C12.3904 20.6781 12.2122 20.75 11.9998 20.75ZM4 18.75C3.7875 18.75 3.60942 18.6781 3.46575 18.5343C3.32192 18.3904 3.25 18.2122 3.25 17.9998C3.25 17.7871 3.32192 17.609 3.46575 17.4655C3.60942 17.3218 3.7875 17.25 4 17.25H8C8.2125 17.25 8.39058 17.3219 8.53425 17.4658C8.67808 17.6096 8.75 17.7878 8.75 18.0003C8.75 18.2129 8.67808 18.391 8.5345 18.5345C8.39058 18.6782 8.2125 18.75 8 18.75H4ZM7.99975 14.75C7.78708 14.75 7.609 14.6781 7.4655 14.5343C7.32183 14.3906 7.25 14.2125 7.25 14V12.75H4C3.7875 12.75 3.60942 12.6781 3.46575 12.5343C3.32192 12.3904 3.25 12.2122 3.25 11.9998C3.25 11.7871 3.32192 11.609 3.46575 11.4655C3.60942 11.3218 3.7875 11.25 4 11.25H7.25V10C7.25 9.7875 7.32192 9.60942 7.46575 9.46575C7.60958 9.32192 7.78775 9.25 8.00025 9.25C8.21292 9.25 8.391 9.32192 8.5345 9.46575C8.67817 9.60942 8.75 9.7875 8.75 10V14C8.75 14.2125 8.67808 14.3906 8.53425 14.5343C8.39042 14.6781 8.21225 14.75 7.99975 14.75ZM12 12.75C11.7875 12.75 11.6094 12.6781 11.4658 12.5343C11.3219 12.3904 11.25 12.2122 11.25 11.9998C11.25 11.7871 11.3219 11.609 11.4658 11.4655C11.6094 11.3218 11.7875 11.25 12 11.25H20C20.2125 11.25 20.3906 11.3219 20.5343 11.4658C20.6781 11.6096 20.75 11.7878 20.75 12.0003C20.75 12.2129 20.6781 12.391 20.5343 12.5345C20.3906 12.6782 20.2125 12.75 20 12.75H12ZM15.9998 8.75C15.7871 8.75 15.609 8.67808 15.4655 8.53425C15.3218 8.39058 15.25 8.2125 15.25 8V4C15.25 3.7875 15.3219 3.60942 15.4658 3.46575C15.6096 3.32192 15.7878 3.25 16.0003 3.25C16.2129 3.25 16.391 3.32192 16.5345 3.46575C16.6782 3.60942 16.75 3.7875 16.75 4V5.25H20C20.2125 5.25 20.3906 5.32192 20.5343 5.46575C20.6781 5.60958 20.75 5.78775 20.75 6.00025C20.75 6.21292 20.6781 6.391 20.5343 6.5345C20.3906 6.67817 20.2125 6.75 20 6.75H16.75V8C16.75 8.2125 16.6781 8.39058 16.5343 8.53425C16.3904 8.67808 16.2122 8.75 15.9998 8.75ZM4 6.75C3.7875 6.75 3.60942 6.67808 3.46575 6.53425C3.32192 6.39042 3.25 6.21225 3.25 5.99975C3.25 5.78708 3.32192 5.609 3.46575 5.4655C3.60942 5.32183 3.7875 5.25 4 5.25H12C12.2125 5.25 12.3906 5.32192 12.5343 5.46575C12.6781 5.60958 12.75 5.78775 12.75 6.00025C12.75 6.21292 12.6781 6.391 12.5343 6.5345C12.3906 6.67817 12.2125 6.75 12 6.75H4Z" fill="#F00D0D"/>
        </g>
      </svg>
      </span> Filter`;
    filterToggle.setAttribute('aria-label', 'Toggle filter options');

    // Insert the toggle button at the top of the content
    const contentContainer = document.querySelector('.bonus-offer__content');
    if (contentContainer) {
      contentContainer.insertAdjacentElement('afterbegin', filterToggle);
    } else {
      // Fallback: insert before filter container
      filterContainer.parentNode.insertBefore(filterToggle, filterContainer);
    }

    // Add event listener to toggle the filter panel
    filterToggle.addEventListener('click', toggleFilterPanel);
  }

  // Convert string filter values to arrays if needed
  const brandFilters = currentFilters.brand
    ? Array.isArray(currentFilters.brand)
      ? currentFilters.brand
      : [currentFilters.brand]
    : [];

  const categoryFilters = currentFilters.category
    ? Array.isArray(currentFilters.category)
      ? currentFilters.category
      : [currentFilters.category]
    : [];

  // Check if any filters are active
  const hasActiveFilters = brandFilters.length > 0 || categoryFilters.length > 0;

  // Get counts for all filter values
  const { brandCounts, categoryCounts } = countItemsPerFilterValue();

  // Create the filter HTML structure with Apply button for mobile and toggleable sections
  filterContainer.innerHTML = `
    <div class="bonus-offer__filter-header">
      <h4 class="bonus-offer__filter-title">Filter</h4>
      <button class="bonus-offer__filter-close" aria-label="Close filter panel">
        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <mask id="mask0_4125_29989" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="25" height="24">
          <rect x="0.5" width="24" height="24" fill="#D9D9D9"/>
          </mask>
          <g mask="url(#mask0_4125_29989)">
          <path d="M12.4995 13.0538L7.42652 18.127C7.28802 18.2653 7.11394 18.3362 6.90427 18.3395C6.69477 18.3427 6.51752 18.2718 6.37252 18.127C6.22769 17.982 6.15527 17.8063 6.15527 17.6C6.15527 17.3937 6.22769 17.218 6.37252 17.073L11.4458 12L6.37252 6.92701C6.23419 6.78851 6.16336 6.61443 6.16002 6.40476C6.15686 6.19526 6.22769 6.01801 6.37252 5.87301C6.51752 5.72818 6.69319 5.65576 6.89952 5.65576C7.10586 5.65576 7.28152 5.72818 7.42652 5.87301L12.4995 10.9463L17.5725 5.87301C17.711 5.73468 17.8851 5.66385 18.0948 5.66051C18.3043 5.65735 18.4815 5.72818 18.6265 5.87301C18.7714 6.01801 18.8438 6.19368 18.8438 6.40001C18.8438 6.60635 18.7714 6.78201 18.6265 6.92701L13.5533 12L18.6265 17.073C18.7649 17.2115 18.8357 17.3856 18.839 17.5953C18.8422 17.8048 18.7714 17.982 18.6265 18.127C18.4815 18.2718 18.3059 18.3443 18.0995 18.3443C17.8932 18.3443 17.7175 18.2718 17.5725 18.127L12.4995 13.0538Z" fill="#7E3324"/>
          </g>
        </svg>
      </button>
    </div>
    <div class="bonus-offer__filter-content">
      <div class="bonus-offer__filter-section">
        <div class="bonus-offer__filter-section-header" data-target="brand-filters">
          <label class="bonus-offer__filter-section-title">Brand</label>
          <span class="bonus-offer__filter-section-toggle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_1306_29633" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
            <rect y="-0.5" width="24" height="24" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_1306_29633)">
            <path d="M11.9999 14.5538L6.34619 8.9L7.39994 7.84625L11.9999 12.4463L16.5999 7.84625L17.6537 8.9L11.9999 14.5538Z" fill="#F00D0D"/>
            </g>
            </svg>
          </span>
        </div>
        <div class="bonus-offer__filter-options" id="brand-filters">
          ${generateFilterOptions(filterOptions.brands, brandFilters, filterOptions.brandLabels, brandCounts, 'brand')}
        </div>
      </div>

      <div class="bonus-offer__filter-section">
        <div class="bonus-offer__filter-section-header" data-target="category-filters">
          <label class="bonus-offer__filter-section-title">Category</label>
          <span class="bonus-offer__filter-section-toggle">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_1306_29633" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
            <rect y="-0.5" width="24" height="24" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_1306_29633)">
            <path d="M11.9999 14.5538L6.34619 8.9L7.39994 7.84625L11.9999 12.4463L16.5999 7.84625L17.6537 8.9L11.9999 14.5538Z" fill="#F00D0D"/>
            </g>
            </svg>
          </span>
        </div>
        <div class="bonus-offer__filter-options" id="category-filters">
          ${generateFilterOptions(
    filterOptions.categories,
    categoryFilters,
    filterOptions.categoryLabels,
    categoryCounts,
    'category'
  )}
        </div>
      </div>
      
      <div class="bonus-offer__filter-actions ${hasActiveFilters ? 'show' : ''}">
        <button type="button" class="bonus-offer__filter-apply button button--primary">Apply Filters</button>
        <button type="button" class="bonus-offer__filter-clear button button--small button--secondary">Clear Filters</button>
      </div>
    </div>
  `;

  // Add event listeners to checkboxes (don't auto-apply on mobile)
  const checkboxes = filterContainer.querySelectorAll('input[type="checkbox"]');

  // For desktop, we'll keep the auto-filter behavior but preserve expanded state
  if (window.innerWidth > 768) {
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', utilityFunctions.throttle(() => {
        // Store current expanded state before applying filters
        const brandSection = document.getElementById('brand-filters');
        const categorySection = document.getElementById('category-filters');

        if (brandSection) {
          expandedSections.brand = brandSection.classList.contains('show-all');
        }
        if (categorySection) {
          expandedSections.category = categorySection.classList.contains('show-all');
        }

        // Apply filters with throttling
        applyFilters(expandedSections);
      }, 200)); // 200ms throttle
    });
  }
  // Clear filters button
  const clearButton = filterContainer.querySelector('.bonus-offer__filter-clear');
  clearButton.addEventListener('click', () => {
    // Show loading for clear action with visual feedback
    loadingManager.show();

    // Add visual feedback for filtering
    const listContainer = document.querySelector('.bonus-offer__list');
    if (listContainer) {
      listContainer.classList.add('is-filtering');
    }

    // Uncheck all filter checkboxes
    const allCheckboxes = filterContainer.querySelectorAll('input[type="checkbox"]');
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Reset filters in app state
    appState.filters = {
      brand: [],
      category: []
    };

    // Reset to all metaobjects
    appState.filteredMetaobjects = [...appState.allMetaobjects];

    // Update URL without reloading the page - use clean URL format
    const searchParams = new URLSearchParams();
    searchParams.set('page', '1');
    window.history.pushState({}, '', `?${searchParams.toString()}`);

    // Reset to page 1 and reload content with smooth transition
    appState.currentPage = 1;

    // Use setTimeout to ensure smooth transition and minimum loading visibility
    setTimeout(() => {
      // Remove filtering visual state
      if (listContainer) {
        listContainer.classList.remove('is-filtering');
      }

      loadPage(1);

      // Close filter panel on mobile
      if (window.innerWidth <= 768) {
        toggleFilterPanel(null, false);
      }
    }, 600); // Increased delay to ensure loading spinner is visible
  });
  // Apply filters button (mainly for mobile)
  const applyButton = filterContainer.querySelector('.bonus-offer__filter-apply');
  applyButton.addEventListener('click', () => {
    // Show loading before applying filters
    loadingManager.show();

    // Add visual feedback
    const listContainer = document.querySelector('.bonus-offer__list');
    if (listContainer) {
      listContainer.classList.add('is-filtering');
    }

    // Apply filters with a slight delay to ensure loading is visible
    setTimeout(() => {
      applyFilters();
      toggleFilterPanel(null, false); // Close the panel after applying
    }, 200);
  });

  // Close button
  const closeButton = filterContainer.querySelector('.bonus-offer__filter-close');
  closeButton.addEventListener('click', () => {
    toggleFilterPanel(null, false); // Force close
  });

  // Add event listeners to section headers to toggle content
  const sectionHeaders = filterContainer.querySelectorAll('.bonus-offer__filter-section-header');
  sectionHeaders.forEach((header) => {
    header.addEventListener('click', () => {
      toggleFilterSection(header);
    });
  });

  // Add event listeners to "Show All" buttons
  const showAllButtons = filterContainer.querySelectorAll('.bonus-offer__filter-show-all');
  showAllButtons.forEach((button) => {
    button.addEventListener('click', function () {
      const targetId = this.getAttribute('data-target');
      const optionsContainer = document.getElementById(targetId);

      if (!optionsContainer) return;

      const isExpanded = optionsContainer.classList.contains('show-all');

      if (isExpanded) {
        // Hide extra options
        optionsContainer.classList.remove('show-all');
        this.classList.remove('showing-all');
      } else {
        // Show all options
        optionsContainer.classList.add('show-all');
        this.classList.add('showing-all');
      }
    });
  });

  // Restore expanded state
  setTimeout(() => {
    const brandSection = document.getElementById('brand-filters');
    const categorySection = document.getElementById('category-filters');

    if (brandSection && expandedSections.brand) {
      brandSection.classList.add('show-all');
      const button = filterContainer.querySelector('button[data-target="brand-filters"]');
      if (button) button.classList.add('showing-all');
    }

    if (categorySection && expandedSections.category) {
      categorySection.classList.add('show-all');
      const button = filterContainer.querySelector('button[data-target="category-filters"]');
      if (button) button.classList.add('showing-all');
    }
  }, 0);

  // Initialize panel state
  updateFilterPanelState();
}

// Function to toggle filter section visibility
function toggleFilterSection(headerElement) {
  const targetId = headerElement.getAttribute('data-target');
  const targetSection = document.getElementById(targetId);

  if (!targetSection) return;

  const isCollapsed = targetSection.classList.contains('collapsed');
  const toggleIcon = headerElement.querySelector('.bonus-offer__filter-section-toggle');

  // Store SVG icons for expand/collapse states
  const expandIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_1306_29633" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
      <rect y="-0.5" width="24" height="24" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#mask0_1306_29633)">
      <path d="M11.9999 14.5538L6.34619 8.9L7.39994 7.84625L11.9999 12.4463L16.5999 7.84625L17.6537 8.9L11.9999 14.5538Z" fill="#F00D0D"/>
    </g>
  </svg>`;

  const collapseIcon = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_1306_29627" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="-1" width="24" height="25">
      <rect y="-0.5" width="24" height="24" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#mask0_1306_29627)">
      <path d="M11.9999 9.95375L7.39994 14.5538L6.34619 13.5L11.9999 7.84625L17.6537 13.5L16.5999 14.5538L11.9999 9.95375Z" fill="#F00D0D"/>
    </g>
  </svg>`;

  if (isCollapsed) {
    // Expand the section
    targetSection.classList.remove('collapsed');
    headerElement.setAttribute('aria-expanded', 'true');
    if (toggleIcon) {
      toggleIcon.innerHTML = expandIcon;
      toggleIcon.setAttribute('aria-label', 'Collapse section');
    }
  } else {
    // Collapse the section    
    targetSection.classList.add('collapsed');
    headerElement.setAttribute('aria-expanded', 'false');
    if (toggleIcon) {
      toggleIcon.innerHTML = collapseIcon;
      toggleIcon.setAttribute('aria-label', 'Expand section');
    }
  }
}

// Function to toggle filter panel visibility
function toggleFilterPanel(event, forceState) {
  // If force state is provided, use it; otherwise toggle current state
  uiState.isFilterOpen = forceState !== undefined ? forceState : !uiState.isFilterOpen;
  updateFilterPanelState();

  // If opening the panel, create an overlay
  if (uiState.isFilterOpen) {
    createFilterOverlay();
  } else {
    removeFilterOverlay();
  }
}

// Function to update the filter panel state based on uiState
function updateFilterPanelState() {
  const filterContainer = document.querySelector('.bonus-offer__filter');
  const body = document.body;

  if (uiState.isFilterOpen) {
    filterContainer.classList.add('is-active');
    body.classList.add('filter-open'); // Prevent scrolling
  } else {
    filterContainer.classList.remove('is-active');
    body.classList.remove('filter-open');
  }
}

// Create overlay when filter is open on mobile
function createFilterOverlay() {
  // Remove existing overlay if any
  removeFilterOverlay();

  // Create new overlay
  const overlay = document.createElement('div');
  overlay.className = 'bonus-offer__filter-overlay';
  overlay.addEventListener('click', () => {
    toggleFilterPanel(null, false); // Close panel when overlay is clicked
  });

  document.body.appendChild(overlay);

  // Animate in
  setTimeout(() => {
    overlay.classList.add('is-active');
  }, 10);
}

// Remove overlay
function removeFilterOverlay() {
  const overlay = document.querySelector('.bonus-offer__filter-overlay');
  if (overlay) {
    overlay.classList.remove('is-active');
    // Wait for animation before removing
    setTimeout(() => {
      overlay.parentNode.removeChild(overlay);
    }, 300);
  }
}

// Function to initialize filter sections
function initializeFilterSections() {
  const sectionHeaders = document.querySelectorAll('.bonus-offer__filter-section-header');

  sectionHeaders.forEach(header => {
    const targetId = header.getAttribute('data-target');
    const targetSection = document.getElementById(targetId);

    if (targetSection) {
      // Set initial state
      header.setAttribute('aria-expanded', 'true');
      targetSection.classList.remove('collapsed');

      // Add click handler
      header.addEventListener('click', () => toggleFilterSection(header));
    }
  });
}
